'use client';

import { useState } from 'react';

interface ActivityEvent {
  id: string;
  type: 'text_sent' | 'text_missed' | 'reply_received' | 'photo_uploaded' | string;
  techName: string;
  customerName: string;
  message: string;
  timestamp: string;
  // Detail fields (present for text_sent)
  photoUrls?: string[];
  mapUrl?: string | null;
  gpsTrail?: boolean;
  geofenceFlag?: boolean;
  geofenceDistance?: number | null;
  serviceSummary?: string;
  serviceCode?: string;
  duration?: number | null;
  customerAddress?: string;
}

interface ActivityFeedProps {
  events: ActivityEvent[];
  loading: boolean;
}

function relativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago',
  });
}

function eventIcon(type: string): string {
  switch (type) {
    case 'text_sent': return '✅';
    case 'text_missed': return '❌';
    case 'missed': return '❌';
    case 'flagged': return '🚩';
    // Not a tech miss — no customer number on file, so no text was possible.
    case 'needs_phone': return '☎️';
    case 'reply_received': return '💬';
    case 'photo_uploaded': return '📷';
    default: return '📋';
  }
}

function eventColor(type: string): string {
  switch (type) {
    case 'text_sent': return 'border-l-green-400';
    case 'text_missed': return 'border-l-red-400';
    case 'missed': return 'border-l-red-400';
    case 'flagged': return 'border-l-orange-400';
    case 'needs_phone': return 'border-l-amber-300';
    case 'reply_received': return 'border-l-blue-400';
    default: return 'border-l-gray-300';
  }
}

function ServiceDetailDrawer({
  event,
  onClose,
}: {
  event: ActivityEvent;
  onClose: () => void;
}) {
  const photos: string[] = Array.isArray(event.photoUrls) ? event.photoUrls : [];
  const [imgError, setImgError] = useState(false);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto">
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-2 pb-3 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{event.customerName}</span>
              {event.gpsTrail && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                  🟠 GPS Trail
                </span>
              )}
              {!event.gpsTrail && event.mapUrl && (
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                  📍 Pin
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">{event.techName}</span>
              {event.serviceCode && <span> · {event.serviceCode}</span>}
              {event.duration && <span> · ⏱ {event.duration} min</span>}
            </div>
            {event.customerAddress && (
              <div className="text-xs text-gray-400 mt-0.5">{event.customerAddress}</div>
            )}
            <div className="text-xs text-gray-400 mt-0.5">{formatTime(event.timestamp)}</div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none pl-4 pt-1"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Satellite map */}
          {event.mapUrl && !imgError ? (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {event.gpsTrail ? '🟠 GPS Trail — Property Walk' : '📍 Service Location'}
              </div>
              <img
                src={event.mapUrl}
                alt="Service location satellite map"
                className="w-full rounded-xl object-cover"
                style={{ maxHeight: 260 }}
                onError={() => setImgError(true)}
              />
            </div>
          ) : imgError ? (
            <div className="text-xs text-gray-400 italic">Map image unavailable</div>
          ) : (
            <div className="text-xs text-gray-400 italic">No map available for this service</div>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                📷 Photos ({photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((url: string, i: number) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={url}
                      alt={`Service photo ${i + 1}`}
                      className="w-full aspect-square object-cover rounded-lg hover:opacity-90 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Service summary */}
          {event.serviceSummary && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                📝 Service Summary
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-xl p-3">
                {event.serviceSummary}
              </p>
            </div>
          )}

          {!event.mapUrl && photos.length === 0 && !event.serviceSummary && (
            <div className="text-center text-gray-400 py-4 text-sm">
              No detail available for this event
            </div>
          )}
        </div>

        <div className="h-6" /> {/* bottom padding */}
      </div>
    </>
  );
}

export default function ActivityFeed({ events, loading }: ActivityFeedProps) {
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="h-5 bg-gray-200 rounded w-32 mb-4 animate-pulse" />
        {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-14 bg-gray-100 rounded mb-2 animate-pulse" />)}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Activity Feed</h2>
          <p className="text-xs text-gray-400 mt-0.5">Tap a ✅ row to view photos & service summary</p>
        </div>
        <div className="overflow-y-auto max-h-[500px] divide-y divide-gray-50">
          {events.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No activity yet today</div>
          ) : (
            events.map(evt => {
              const hasDetail = evt.type === 'text_sent' && (evt.mapUrl || (evt.photoUrls && evt.photoUrls.length > 0) || evt.serviceSummary);
              return (
                <div
                  key={evt.id}
                  className={`px-4 py-3 border-l-4 ${eventColor(evt.type)} ${hasDetail ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100' : ''}`}
                  onClick={() => hasDetail ? setSelectedEvent(evt) : undefined}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-base mt-0.5">{eventIcon(evt.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800">
                        <span className="font-medium">{evt.techName}</span>
                        {' · '}
                        <span className="text-gray-500">{evt.customerName}</span>
                        {evt.type === 'text_sent' && evt.gpsTrail && (
                          <span className="ml-1 text-xs text-orange-500">🟠</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{evt.message}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {hasDetail && <span className="text-xs text-gray-300">›</span>}
                      <span className="text-xs text-gray-400 whitespace-nowrap">{relativeTime(evt.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selectedEvent && (
        <ServiceDetailDrawer
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </>
  );
}

export type { ActivityEvent };
